'use client';

import { Fragment, useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CdCalculator() {
  const t = useTranslations("tools.cdCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    initialDeposit: 10000,
    interestRate: 5,
    compoundFrequency: 12,
    years: 3,
    months: 0,
    taxRate: 0,
  });

  const [results, setResults] = useState<{
    endBalance: number;
    totalInterest: number;
    interestAfterTax: number;
    depositPercent: number;
    interestPercent: number;
  } | null>(null);

  const [schedule, setSchedule] = useState<Array<{
    month: number;
    deposit: number;
    interest: number;
    balance: number;
    isYearEnd: boolean;
  }>>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    calculateCD();
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number): string => {
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const calculateCD = () => {
    const principal = inputs.initialDeposit;
    const annualRate = inputs.interestRate / 100;
    const compoundFreq = inputs.compoundFrequency;
    const totalMonths = inputs.years * 12 + inputs.months;
    const totalYears = totalMonths / 12;
    const taxRate = inputs.taxRate / 100;

    if (principal <= 0 || totalYears <= 0) {
      return;
    }

    const rate = annualRate / compoundFreq;
    const periods = compoundFreq * totalYears;
    const endBalance = principal * Math.pow(1 + rate, periods);
    const totalInterest = endBalance - principal;
    const taxAmount = totalInterest * taxRate;
    const interestAfterTax = totalInterest - taxAmount;

    const depositPercent = (principal / endBalance * 100);
    const interestPercent = (totalInterest / endBalance * 100);

    setResults({
      endBalance,
      totalInterest,
      interestAfterTax,
      depositPercent,
      interestPercent,
    });

    generateSchedule(principal, annualRate, totalMonths);
  };

  const generateSchedule = (principal: number, annualRate: number, totalMonths: number) => {
    const scheduleData = [];
    let balance = principal;
    const monthlyRate = annualRate / 12;

    for (let month = 1; month <= Math.min(totalMonths, 36); month++) {
      const interest = balance * monthlyRate;
      balance += interest;

      scheduleData.push({
        month,
        deposit: month === 1 ? principal : 0,
        interest,
        balance,
        isYearEnd: month % 12 === 0,
      });
    }

    setSchedule(scheduleData);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    calculateCD();
  };

  const handleInputChange = (field: string, value: number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M", x, y,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1100px' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.05rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const selectStyle = { ...inputStyle };

  const inputRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const inputWithUnitStyle = { position: 'relative' as const };

  const inputUnitStyle = {
    position: 'absolute' as const,
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
    pointerEvents: 'none' as const,
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '0.5rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1.125rem 2.5rem', fontSize: '1.125rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1rem',
    marginBottom: '0.75rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #e2e8f0',
  };

  const resultItemHighlightStyle = {
    ...resultItemStyle,
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    borderLeft: '4px solid #2563eb',
  };

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#475569' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' }
  );

  const chartContainerStyle = getResponsiveStyle(
    { marginTop: '2rem', padding: '1.5rem', background: '#f8fafc', borderRadius: '0.75rem' },
    {},
    {},
    {}
  );

  const pieChartStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '1.5rem',
  };

  const chartLegendStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '2rem',
    flexWrap: 'wrap' as const,
  };

  const legendItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const legendColorStyle = {
    width: '1rem',
    height: '1rem',
    borderRadius: '0.25rem',
  };

  const legendTextStyle = getResponsiveStyle(
    { color: '#475569' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' }
  );

  const scheduleSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginTop: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const scheduleTableContainerStyle = {
    width: '100%',
    overflowX: 'auto' as const,
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    minWidth: '600px',
  };

  const thStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      textAlign: 'left' as const,
      fontWeight: 600,
    },
    { padding: '0.75rem', fontSize: '0.9rem' },
    { padding: '0.875rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const tdStyle = getResponsiveStyle(
    { borderBottom: '1px solid #e2e8f0' },
    { padding: '0.625rem 0.75rem', fontSize: '0.85rem' },
    { padding: '0.75rem 0.875rem', fontSize: '0.9rem' },
    { padding: '0.875rem 1rem', fontSize: '0.95rem' }
  );

  const yearEndRowStyle = {
    background: '#eff6ff',
    fontWeight: 600,
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
  };

  const listItemStyle = {
    marginBottom: '0.75rem',
  };

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      border: '1px solid rgba(255, 200, 180, 0.15)',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.2rem' }
  );

  const faqAnswerStyle = {
    ...paragraphStyle,
    marginBottom: 0,
  };

  const referenceStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0.5rem', lineHeight: 1.6 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-all' as const,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={{ textAlign: 'center' as const, marginBottom: '2rem' }}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={formCardStyle}>
              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label htmlFor="initial-deposit" style={labelStyle}>{t("initialDepositLabel")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="initial-deposit"
                      value={inputs.initialDeposit}
                      onChange={(e) => handleInputChange('initialDeposit', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      required
                      style={inputStyle}
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="interest-rate" style={labelStyle}>{t("interestRateLabel")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="interest-rate"
                      value={inputs.interestRate}
                      onChange={(e) => handleInputChange('interestRate', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      max="100"
                      required
                      style={inputStyle}
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="compound-frequency" style={labelStyle}>{t("compoundFrequencyLabel")}</label>
                  <select
                    id="compound-frequency"
                    value={inputs.compoundFrequency}
                    onChange={(e) => handleInputChange('compoundFrequency', parseInt(e.target.value))}
                    style={selectStyle}
                  >
                    <option value="1">{t("annually")}</option>
                    <option value="2">{t("semiAnnually")}</option>
                    <option value="4">{t("quarterly")}</option>
                    <option value="12">{t("monthly")}</option>
                    <option value="365">{t("daily")}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("depositLengthLabel")}</label>
                  <div style={inputRowStyle}>
                    <div style={inputWithUnitStyle}>
                      <input
                        type="number"
                        id="years"
                        value={inputs.years}
                        onChange={(e) => handleInputChange('years', parseInt(e.target.value) || 0)}
                        min="0"
                        step="1"
                        style={inputStyle}
                      />
                      <span style={inputUnitStyle}>{t("years")}</span>
                    </div>
                    <div style={inputWithUnitStyle}>
                      <input
                        type="number"
                        id="months"
                        value={inputs.months}
                        onChange={(e) => handleInputChange('months', parseInt(e.target.value) || 0)}
                        min="0"
                        max="11"
                        step="1"
                        style={inputStyle}
                      />
                      <span style={inputUnitStyle}>{t("months")}</span>
                    </div>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="tax-rate" style={labelStyle}>{t("taxRateLabel")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="tax-rate"
                      value={inputs.taxRate}
                      onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      max="100"
                      style={inputStyle}
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>

                <button
                  type="submit"
                  style={calculateButtonStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(37, 99, 235, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            <div style={formCardStyle}>
              <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.5rem' }}>{t("resultsTitle")}</h3>
              {results && (
                <>
                  <div style={resultItemHighlightStyle}>
                    <span style={resultLabelStyle}>{t("endBalanceLabel")}</span>
                    <span style={resultValueHighlightStyle}>{formatCurrency(results.endBalance)}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("totalInterestLabel")}</span>
                    <span style={resultValueStyle}>{formatCurrency(results.totalInterest)}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("interestAfterTaxLabel")}</span>
                    <span style={resultValueStyle}>{formatCurrency(results.interestAfterTax)}</span>
                  </div>

                  <div style={chartContainerStyle}>
                    <div style={pieChartStyle}>
                      <svg width="200" height="200">
                        <path
                          d={describeArc(100, 100, 80, 0, results.depositPercent * 3.6)}
                          fill="#2563eb"
                          stroke="white"
                          strokeWidth="2"
                        />
                        <path
                          d={describeArc(100, 100, 80, results.depositPercent * 3.6, 360)}
                          fill="#10b981"
                          stroke="white"
                          strokeWidth="2"
                        />
                        <circle cx="100" cy="100" r="40" fill="white" />
                        <text
                          x="100"
                          y="100"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="24"
                          fontWeight="bold"
                          fill="#1e293b"
                        >
                          CD
                        </text>
                      </svg>
                    </div>
                    <div style={chartLegendStyle}>
                      <div style={legendItemStyle}>
                        <div style={{ ...legendColorStyle, background: '#2563eb' }}></div>
                        <span style={legendTextStyle}>
                          {t("initialDepositLegend")} ({results.depositPercent.toFixed(1)}%)
                        </span>
                      </div>
                      <div style={legendItemStyle}>
                        <div style={{ ...legendColorStyle, background: '#10b981' }}></div>
                        <span style={legendTextStyle}>
                          {t("interestLegend")} ({results.interestPercent.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {schedule.length > 0 && (
            <div style={scheduleSectionStyle}>
              <h3 style={h2Style}>{t("scheduleTitle")}</h3>
              <div style={scheduleTableContainerStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t("monthColumn")}</th>
                      <th style={thStyle}>{t("depositColumn")}</th>
                      <th style={thStyle}>{t("interestColumn")}</th>
                      <th style={thStyle}>{t("endingBalanceColumn")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((row, index) => (
                      <Fragment key={index}>
                        <tr
                          style={row.isYearEnd ? yearEndRowStyle : {}}
                          onMouseEnter={(e) => {
                            if (!row.isYearEnd) {
                              e.currentTarget.style.background = '#f8fafc';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!row.isYearEnd) {
                              e.currentTarget.style.background = 'transparent';
                            }
                          }}
                        >
                          <td style={tdStyle}>{row.month}</td>
                          <td style={tdStyle}>{formatCurrency(row.deposit)}</td>
                          <td style={tdStyle}>{formatCurrency(row.interest)}</td>
                          <td style={tdStyle}>{formatCurrency(row.balance)}</td>
                        </tr>
                        {row.isYearEnd && (
                          <tr style={yearEndRowStyle}>
                            <td colSpan={4} style={{ ...tdStyle, textAlign: 'center' as const }}>
                              {t("endOfYear")} {row.month / 12}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToUseStep1")}</li>
          <li style={listItemStyle}>{t("howToUseStep2")}</li>
          <li style={listItemStyle}>{t("howToUseStep3")}</li>
          <li style={listItemStyle}>{t("howToUseStep4")}</li>
          <li style={listItemStyle}>{t("howToUseStep5")}</li>
          <li style={listItemStyle}>{t("howToUseStep6")}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>

        <h3 style={h3Style}>{t("insightsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>

        <h3 style={h3Style}>{t("insightsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insightsBullet1")}</li>
          <li style={listItemStyle}>{t("insightsBullet2")}</li>
          <li style={listItemStyle}>{t("insightsBullet3")}</li>
          <li style={listItemStyle}>{t("insightsBullet4")}</li>
        </ul>

        <h3 style={h3Style}>{t("insightsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("insightsParagraph3")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insightsBullet5")}</li>
          <li style={listItemStyle}>{t("insightsBullet6")}</li>
          <li style={listItemStyle}>{t("insightsBullet7")}</li>
          <li style={listItemStyle}>{t("insightsBullet8")}</li>
          <li style={listItemStyle}>{t("insightsBullet9")}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle4")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph4")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle5")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph5")}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion1")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer1")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion2")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer2")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion3")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer3")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion4")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer4")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion5")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer5")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion6")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer6")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion7")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer7")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <p style={referenceStyle}>
          [1] <a href="https://www.calculator.net/cd-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Calculator.net - CD Calculator
          </a>
        </p>
        <p style={referenceStyle}>
          [2] <a href="https://www.bankrate.com/banking/cds/cd-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Bankrate - CD Calculator
          </a>
        </p>
        <p style={referenceStyle}>
          [3] <a href="https://www.nerdwallet.com/banking/calculator/cd-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            NerdWallet - CD Calculator
          </a>
        </p>
        <p style={referenceStyle}>
          [4] <a href="https://www.investopedia.com/terms/c/certificateofdeposit.asp" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Investopedia - Certificate of Deposit (CD)
          </a>
        </p>
        <p style={referenceStyle}>
          [5] <a href="https://www.fdic.gov/resources/deposit-insurance/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            FDIC - Deposit Insurance
          </a>
        </p>
        <p style={referenceStyle}>
          [6] <a href="https://www.consumerfinance.gov/ask-cfpb/what-is-a-certificate-of-deposit-cd-en-2033/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Consumer Financial Protection Bureau - What is a CD?
          </a>
        </p>
      </section>
    </div>
  );
}
